import styled from "@emotion/styled";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AdminIcon,
  CameraIcon,
  TrashCanIcon,
} from "../../assets/icons/icons";
import { MemberDetail } from "../../types/types";
import { InfoType } from "./userModifyModal";
import SelectBox, { menuItemType } from "../selectBox";
import { SelectChangeEvent } from "@mui/material";
import useGetUserGroup from "../../hooks/useGetUserGroup";
import theme from "../../theme";
import NominatimPost from "../NominatimPost";
import { useI18n } from "../../locales";
import DaumPost from "../DaumPost";
import {
  checkPassword,
  isValidEmail,
  isValidPhone,
} from "../../utils/util";
import { useWindowSize } from "@reactuses/core";
import { Label } from "../common/Label";
import { TextInput } from "../common/TextInput";

const { sm, md, lg } = theme.screen;

const InputField: React.FC<{
  data?: MemberDetail;
  info: InfoType;
  setInfo: Dispatch<SetStateAction<InfoType>>;
  imgFile: string;
  setImgFile: Dispatch<SetStateAction<string>>;
  imgData: FormData;
  setImgData: Dispatch<SetStateAction<any>>;
  setImgCancel: Dispatch<SetStateAction<boolean>>;
  isMyPage?: boolean;
  pwCheck: string;
  setPwCheck: React.Dispatch<React.SetStateAction<string>>;
}> = (props) => {
  const {
    data,
    info,
    setInfo,
    imgFile,
    setImgFile,
    setImgData,
    setImgCancel,
    isMyPage,
    pwCheck,
    setPwCheck,
  } = props;
  const { width } = useWindowSize();
  const { t, locale } = useI18n();

  const fileUploadRef = useRef<HTMLInputElement>(null);

  const { data: GroupOptions } = useGetUserGroup({});
  const userGroupType: menuItemType[] = [];
  if (GroupOptions) {
    for (let i = 0; i < GroupOptions.length; i++) {
      userGroupType.push({
        value: GroupOptions[i].group_id,
        title: GroupOptions[i].name,
      });
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleChangeSelectBox = (event: SelectChangeEvent<String>) => {
    setInfo((prevState) => ({
      ...prevState,
      group_id: event.target.value.toString(),
    }));
  };

  useEffect(() => {
    if (data) {
      setInfo({
        id: data.id ?? "",
        pw: data.pw ?? "",
        name: data.name ?? "",
        phone: data.phone ?? "",
        email: data.email ?? "",
        address: data.address ?? "",
        address_detail: data.address_detail ?? "",
        group_id: data.gov_id ?? "",
        admin_image: data.admin_image ?? "",
      });
    }
  }, [data, setInfo]);

  const handleFileUpload = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };
  const handleFileCancel = () => {
    setInfo((prevState) => ({
      ...prevState,
      admin_image: "",
    }));
    setImgFile("");
    setImgData(null);
    setImgCancel(true);
  };

  const handleFileInputChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (reader.result !== null) {
          setImgFile(reader.result as string);
        }
      };

      setImgData(file); // 테스트
    }
  };

  const [popup, setPopup] = useState(false);

  const handleComplete = () => {
    setPopup(!popup);
  };

  // console.log(info);
  // console.log(checkPassword({ pw: info.pw, pwCheck }));

  return (
    <UserInfoWrapper>
      <input
        style={{ display: "none" }}
        type="file"
        ref={fileUploadRef}
        accept=".jpg,.jpeg,.png"
        onChange={handleFileInputChange}
      />
      <ImageWrapper>
        {imgFile ? (
          <CustomImg src={imgFile} alt="사용자 이미지" />
        ) : info.admin_image ? (
          <CustomImg
            src={`${process.env.REACT_APP_BASE_URL}/${info.admin_image}`}
            alt="사용자 이미지"
          />
        ) : (
          <AdminIcon width={167} height={167} />
        )}
        {imgFile ? (
          <IconWrapper2 onClick={handleFileCancel}>
            <TrashCanIcon />
          </IconWrapper2>
        ) : info.admin_image ? (
          <IconWrapper2 onClick={handleFileCancel}>
            <TrashCanIcon />
          </IconWrapper2>
        ) : null}
        <IconWrapper onClick={handleFileUpload}>
          <CameraIcon />
        </IconWrapper>
      </ImageWrapper>

      <UserInfo>
        {/* screen sm ~ lg */}
        {width < lg && (
          <>
            <RowWrapper>
              <InputWrapper>
                <Label text={t.manageUser.userId} htmlFor="id" />
                <TextInput
                  disabled
                  value={info.id}
                  readOnly
                  id="id"
                  name="id"
                  width={"100%"}
                />
              </InputWrapper>
              <InputWrapper style={{ position: "relative" }}>
                <Label text={t.login.password} htmlFor="pw" />
                <TextInput
                  placeholder={t.manageUser.passwordRequirements}
                  onChange={handleChange}
                  value={info.pw}
                  minLength={6}
                  id="pw"
                  name="pw"
                  width={"100%"}
                  type="password"
                />
                {checkPassword({ pw: info.pw, pwCheck }).pwError && (
                  <PwError>
                    {checkPassword({ pw: info.pw, pwCheck }).pwErrorReason}
                  </PwError>
                )}
              </InputWrapper>
              <InputWrapper style={{ position: "relative" }}>
                <Label text={t.manageUser.passwordConfirm} htmlFor="pwCheck" />
                <TextInput
                  placeholder={t.manageUser.passwordRequirements}
                  onChange={(e) => setPwCheck(e.target.value)}
                  value={pwCheck}
                  minLength={6}
                  id="pwCheck"
                  name="pwCheck"
                  width={"100%"}
                  type="password"
                />
                {checkPassword({ pw: info.pw, pwCheck }).pwCheckError && (
                  <PwError>
                    {
                      checkPassword({ pw: info.pw, pwCheck })
                        .pwCheckErrorReason
                    }
                  </PwError>
                )}
              </InputWrapper>
            </RowWrapper>
            <RowWrapper>
              <InputWrapper>
                <Label text={t.manageUser.email} htmlFor="email" />
                <TextInput
                  onChange={handleChange}
                  value={info.email}
                  id="email"
                  name="email"
                  width={"100%"}
                  placeholder={t.manageUser.emailMaxCharacters}
                  maxLength={50}
                />
                {!isValidEmail(info.email) && (
                  <PwError>{t.manageUser.emailExample}</PwError>
                )}
              </InputWrapper>
              <InputWrapper style={{ position: "relative" }}>
                <Label text={t.manageUser.contact} htmlFor="phone" />
                <TextInput
                  onChange={handleChange}
                  value={info.phone}
                  id="phone"
                  name="phone"
                  width={"100%"}
                  placeholder={t.manageUser.phoneIncludeDash}
                  minLength={10}
                  maxLength={13}
                />
                {!isValidPhone(info.phone) && (
                  <PwError>{t.manageUser.phoneExample}</PwError>
                )}
              </InputWrapper>
            </RowWrapper>
            <RowWrapper>
              <InputWrapper>
                {/* <Label text="담당기관" htmlFor="group_id" isRequired /> */}
                <Label text={t.manageUser.area} htmlFor="group_id" isRequired />
                <SelectBox
                  disabled={isMyPage && data?.grade !== 2}
                  filter={info?.group_id || "00000"}
                  handleChange={handleChangeSelectBox}
                  menuItems={userGroupType}
                  width={"100%"}
                  useSearch
                  srLabel={t.manageUser.area}
                ></SelectBox>
              </InputWrapper>
              <InputWrapper>
                <Label text={t.manageUser.accountName} htmlFor="name" isRequired />
                <TextInput
                  onChange={handleChange}
                  value={info.name}
                  id="name"
                  name="name"
                  width={"100%"}
                  placeholder={t.manageUser.accountNamePlaceholder}
                  maxLength={20}
                />
              </InputWrapper>
            </RowWrapper>
            <RowWrapper>
              <InputWrapper>
                <Label text={t.manageUser.address} htmlFor="address">
                  <PostFindBtn onClick={handleComplete}>
                    {t.manageUser.postcodeSearch}
                  </PostFindBtn>
                  <PostFindBtn
                    onClick={() =>
                      setInfo((prevState) => ({
                        ...prevState,
                        address: "",
                      }))
                    }
                  >
                    {t.manageUser.clear}
                  </PostFindBtn>
                </Label>
                <TextInput
                  onChange={handleChange}
                  value={info.address}
                  id="address"
                  name="address"
                  width={"100%"}
                  placeholder={t.manageUser.addressPlaceholder}
                  readOnly
                />
              </InputWrapper>

              <InputWrapper>
                <Label text={t.manageUser.detailAddress} htmlFor="address_detail" />
                <TextInput
                  onChange={handleChange}
                  value={info.address_detail}
                  id="address_detail"
                  name="address_detail"
                  width={"100%"}
                  placeholder={t.manageUser.detailAddressMaxCharacters}
                  maxLength={100}
                />
              </InputWrapper>
            </RowWrapper>
          </>
        )}

        {/* screen lg ~ */}
        {width >= lg && (
          <>
            <FirstRow>
              <InputWrapper>
                <Label text={t.manageUser.userId} htmlFor="id" />
                <TextInput
                  disabled
                  value={info.id}
                  readOnly
                  id="id"
                  name="id"
                  width={"230px"}
                />
              </InputWrapper>
              <InputWrapper style={{ position: "relative" }}>
                <Label text={t.login.password} htmlFor="pw" />
                <TextInput
                  placeholder={t.manageUser.passwordRequirements}
                  onChange={handleChange}
                  value={info.pw}
                  minLength={6}
                  id="pw"
                  name="pw"
                  width={"230px"}
                  type="password"
                />
                {checkPassword({ pw: info.pw, pwCheck }).pwError && (
                  <PwError>
                    {checkPassword({ pw: info.pw, pwCheck }).pwErrorReason}
                  </PwError>
                )}
              </InputWrapper>
              <InputWrapper style={{ position: "relative" }}>
                <Label text={t.manageUser.passwordConfirm} htmlFor="pwCheck" />
                <TextInput
                  placeholder={t.manageUser.passwordRequirements}
                  onChange={(e) => setPwCheck(e.target.value)}
                  value={pwCheck}
                  minLength={6}
                  id="pwCheck"
                  name="pwCheck"
                  width={"230px"}
                  type="password"
                />
                {checkPassword({ pw: info.pw, pwCheck }).pwCheckError && (
                  <PwError>
                    {
                      checkPassword({ pw: info.pw, pwCheck })
                        .pwCheckErrorReason
                    }
                  </PwError>
                )}
              </InputWrapper>
            </FirstRow>
            <SecondRow>
              <InputWrapper>
                <Label text={t.manageUser.email} htmlFor="email" />
                <TextInput
                  onChange={handleChange}
                  value={info.email}
                  id="email"
                  name="email"
                  width={"230px"}
                  placeholder={t.manageUser.emailMaxCharacters}
                  maxLength={50}
                />
                {!isValidEmail(info.email) && (
                  <PwError>{t.manageUser.emailExample}</PwError>
                )}
              </InputWrapper>
              <InputWrapper style={{ position: "relative" }}>
                <Label text={t.manageUser.contact} htmlFor="phone" />

                <TextInput
                  onChange={handleChange}
                  value={info.phone}
                  id="phone"
                  name="phone"
                  width={"230px"}
                  placeholder={t.manageUser.phoneIncludeDash}
                  minLength={10}
                  maxLength={13}
                />
                {!isValidPhone(info.phone) && (
                  <PwError>{t.manageUser.phoneExample}</PwError>
                )}
              </InputWrapper>
              {/* <div style={{ marginLeft: "10px" }}></div> */}
              <InputWrapper>
                {/* <Label text="담당기관" htmlFor="group_id" isRequired /> */}
                <Label text={t.manageUser.area} htmlFor="group_id" isRequired />
                <SelectBox
                  disabled={isMyPage && data?.grade !== 2}
                  filter={info?.group_id || "00000"}
                  handleChange={handleChangeSelectBox}
                  menuItems={userGroupType}
                  width={230}
                  mr="0px"
                  useSearch
                  srLabel={t.manageUser.area}
                ></SelectBox>
              </InputWrapper>
            </SecondRow>
            <ThirdRow>
              <InputWrapper>
                <Label text={t.manageUser.accountName} htmlFor="name" isRequired />
                <TextInput
                  onChange={handleChange}
                  value={info.name}
                  id="name"
                  name="name"
                  width={"230px"}
                  placeholder={t.manageUser.accountNamePlaceholder}
                  maxLength={20}
                />
              </InputWrapper>
              <InputWrapper>
                <Label text={t.manageUser.address} htmlFor="address">
                  <PostFindBtn onClick={handleComplete}>
                    {t.manageUser.postcodeSearch}
                  </PostFindBtn>
                  <PostFindBtn
                    onClick={() =>
                      setInfo((prevState) => ({
                        ...prevState,
                        address: "",
                      }))
                    }
                  >
                    {t.manageUser.clear}
                  </PostFindBtn>
                </Label>
                <TextInput
                  onChange={handleChange}
                  value={info.address}
                  id="address"
                  name="address"
                  width={"230px"}
                  placeholder={t.manageUser.addressPlaceholder}
                  readOnly
                />
              </InputWrapper>

              <InputWrapper>
                <Label text={t.manageUser.detailAddress} htmlFor="address_detail" />
                <TextInput
                  onChange={handleChange}
                  value={info.address_detail}
                  id="address_detail"
                  name="address_detail"
                  width={"230px"}
                  placeholder={t.manageUser.detailAddressMaxCharacters}
                  maxLength={100}
                />
              </InputWrapper>
            </ThirdRow>
          </>
        )}
        {popup && (
          locale === 'ko' ? (
            <DaumPost
              open={popup}
              close={handleComplete}
              setForm={setInfo}
            />
          ) : (
            <NominatimPost
              open={popup}
              close={handleComplete}
              setForm={setInfo as any}
            />
          )
        )}
      </UserInfo>
    </UserInfoWrapper>
  );
};

const UserInfoWrapper = styled.div`
  background: ${theme.color.neutral_50};
  border-radius: 10px;
  padding-left: 40px;
  padding-right: 30px;
  padding-top: 30px;
  padding-bottom: 30px;
  width: 100%;
  display: flex;
  gap: 44px;
  margin-bottom: 40px;

  @media (max-width: ${sm}px) {
    padding: 10px;
  }

  @media (max-width: ${lg}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const UserInfo = styled.div`
  width: 100%;
`;

const FirstRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 26px;

  @media (max-width: ${lg}px) {
    display: none;
  }
`;

const SecondRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 26px;

  @media (max-width: ${lg}px) {
    display: none;
  }
`;

const ThirdRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;

  @media (max-width: ${lg}px) {
    display: none;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: ${sm}px) {
    width: 100%;
  }
  @media (min-width: ${sm + 1}px) and (max-width: ${md}px) {
    width: 100%;
  }

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    /* width: 50%; */
    width: 100%;
  }
`;

const PwError = styled.p`
  font-size: 10px;
  color: ${theme.color.red_500};
  position: absolute;
  top: 105%;
  left: 3px;
  width: 200px;
  line-height: 12px;
  white-space: pre-line;
`;
const PostFindBtn = styled.button`
  margin-left: 10px;
  height: 25px;
  background: ${theme.color.neutral_200};
  border: 1px solid rgb(0, 0, 0, 0);
  border-radius: 5px;
  cursor: pointer;
  color: ${theme.color.neutral_500};
  &:hover {
    border: 1px solid ${theme.color.neutral_400};
  }

  @media (max-width: ${md}px) {
    height: 20px;
    font-size: ${theme.fontSize.FONT_2};
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  display: flex;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  background: ${theme.color.neutral_50};

  @media (min-width: ${lg + 1}px) {
    width: 167px;
    height: 250px;
  }
`;

const CustomImg = styled.img`
  width: 100%;
  object-fit: contain;

  @media (min-width: ${lg + 1}px) {
    width: 167px;
    height: 250px;
  }
`;

const IconWrapper = styled.button`
  width: 43px;
  height: 43px;
  background: ${theme.color.white};
  border-radius: 50%;
  box-shadow: 0px 1.0705px 4.282px rgba(0, 0, 0, 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 5px;
  right: 5px;
  cursor: pointer;
  &:hover {
    background: ${theme.color.neutral_100};
    box-shadow: 0px 1.0705px 10px rgba(0, 0, 0, 0.2);
  }
`;
const IconWrapper2 = styled.button`
  font-weight: bold;
  color: ${theme.color.neutral_400};
  font-size: 20px;
  width: 43px;
  height: 43px;
  background: ${theme.color.white};
  border-radius: 50%;
  box-shadow: 0px 1.0705px 4.282px rgba(0, 0, 0, 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 5px;
  right: 55px;
  cursor: pointer;
  &:hover {
    background: ${theme.color.neutral_100};
    box-shadow: 0px 1.0705px 10px rgba(0, 0, 0, 0.2);
  }
`;

const RowWrapper = styled.div`
  width: 100%;
  gap: 30px;
  margin-bottom: 30px;
  flex-direction: column;
  display: none;

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    /* flex-direction: row; */
  }

  @media (max-width: ${lg}px) {
    display: flex;
  }
`;

export default InputField;
